import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { PromotionPayload } from '../../../api/generated';
import { TextField } from '../../../components/TextField';
import { Button } from '../../../components/Button';
import { appSettings } from '../../../config/appSettings';
import { ReactComponent as CloseIcon } from '../../../assets/icons/x.svg';

interface PromotionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (promotion: PromotionPayload) => void;
  promotion?: PromotionPayload;
  mode: 'add' | 'edit';
}

// Convert "HH:mm:ss" string to "HH:mm" for time input
const formatTimeOfDay = (time: string | null | undefined): string => {
  if (!time) return '';
  // time is in "HH:mm:ss" format, extract "HH:mm"
  const parts = time.split(':');
  if (parts.length < 2) return '';
  return `${parts[0]}:${parts[1]}`;
};

// Convert "HH:mm" from time input to "HH:mm:ss" string
const parseTimeOfDay = (value: string): string | null => {
  if (!value) return null;
  const [hours, minutes] = value.split(':');
  if (!hours || !minutes) return null;
  return `${hours}:${minutes}:00`;
};

// Format ISO string to datetime-local input value in Copenhagen timezone
const formatDateTime = (isoString: string | null | undefined): string => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    // Format in Copenhagen timezone
    const formatter = new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Europe/Copenhagen',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = formatter.formatToParts(date);
    const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
  } catch {
    return '';
  }
};

// Convert datetime-local input (treated as Copenhagen time) to UTC ISO string
const parseDateTime = (value: string): string | null => {
  if (!value) return null;
  try {
    // Parse the datetime-local value (YYYY-MM-DDTHH:MM)
    const [datePart, timePart] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);

    // Create a reference date at this time in UTC to calculate Copenhagen offset
    const refDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    const copHourStr = refDate.toLocaleString('en-US', {
      timeZone: 'Europe/Copenhagen',
      hour: 'numeric',
      hour12: false,
    });
    const copHour = parseInt(copHourStr);
    const offset = copHour - 12; // Copenhagen's offset from UTC (1 or 2)

    // Create the date in UTC by subtracting Copenhagen's offset
    const utcDate = new Date(Date.UTC(year, month - 1, day, hour - offset, minute, 0));
    return utcDate.toISOString();
  } catch {
    return null;
  }
};

const getInitialPromotion = (mode: 'add' | 'edit'): PromotionPayload => ({
  customerCashbackPercent:
    mode === 'add' ? appSettings.DEFAULT_CUSTOMER_CASHBACK_PERCENT : undefined,
  startDateTime: null,
  endDateTime: null,
  minTimeOfDay: null,
  maxTimeOfDay: null,
  minTransactionAmount: null,
  maxTransactionAmount: null,
});

export const PromotionModal: React.FC<PromotionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  promotion,
  mode,
}) => {
  const [formData, setFormData] = useState<PromotionPayload>(getInitialPromotion(mode));

  useEffect(() => {
    if (isOpen) {
      setFormData(promotion ?? getInitialPromotion(mode));
    }
  }, [isOpen, promotion, mode]);

  const handleCashbackChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setFormData((prev) => ({
      ...prev,
      customerCashbackPercent: isNaN(value) ? undefined : value,
    }));
  };

  const handleDateChange =
    (field: 'startDateTime' | 'endDateTime') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: parseDateTime(e.target.value),
      }));
    };

  const handleTimeOfDayChange =
    (field: 'minTimeOfDay' | 'maxTimeOfDay') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: parseTimeOfDay(e.target.value),
      }));
    };

  const handleAmountChange =
    (field: 'minTransactionAmount' | 'maxTransactionAmount') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      if (inputValue === '') {
        setFormData((prev) => ({ ...prev, [field]: null }));
        return;
      }
      // Restrict to max 2 decimal places
      const regex = /^\d*\.?\d{0,2}$/;
      if (!regex.test(inputValue)) return;
      const value = parseFloat(inputValue);
      setFormData((prev) => ({
        ...prev,
        [field]: isNaN(value) ? null : value,
      }));
    };

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  const handleClose = () => {
    setFormData(getInitialPromotion(mode));
    onClose();
  };

  // Validation
  const cashbackError =
    formData.customerCashbackPercent === undefined ||
    formData.customerCashbackPercent === null ||
    formData.customerCashbackPercent <= 0
      ? 'Cashback percentage is required and must be greater than 0'
      : null;

  const dateTimeError =
    formData.startDateTime &&
    formData.endDateTime &&
    new Date(formData.endDateTime) <= new Date(formData.startDateTime)
      ? 'End date & time must be after start date & time'
      : null;

  const transactionAmountError =
    formData.minTransactionAmount !== null &&
    formData.minTransactionAmount !== undefined &&
    formData.maxTransactionAmount !== null &&
    formData.maxTransactionAmount !== undefined &&
    formData.minTransactionAmount > formData.maxTransactionAmount
      ? 'Minimum amount must be less than or equal to maximum amount'
      : null;

  const isValid = !cashbackError && !dateTimeError && !transactionAmountError;

  const totalCommission =
    (formData.customerCashbackPercent ?? 0) + appSettings.OISTER_COMMISSION_PERCENT;

  return (
    <Modal
      className="modal-content md:max-w-[600px]"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel={mode === 'add' ? 'Add Promotion' : 'Edit Promotion'}
    >
      <div className="flex justify-between mb-6">
        <h2 className="text-primary font-medium text-xl">
          {mode === 'add' ? 'Add Promotion' : 'Edit Promotion'}
        </h2>
        <Button onClick={handleClose} variant="icon">
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <div className="space-y-6">
        {/* Customer Cashback */}
        <div>
          <TextField
            type="number"
            id="cashback"
            name="cashback"
            label="Customer Cashback (1-100%)"
            value={formData.customerCashbackPercent ?? ''}
            onChange={handleCashbackChange}
            min={1}
            max={100}
            step={1}
            required
          />
          {formData.customerCashbackPercent !== undefined && (
            <p className="text-sm text-gray-500 mt-2">
              + {appSettings.OISTER_COMMISSION_PERCENT}% OiSTER Bonus commission ={' '}
              <span className="font-medium">{totalCommission}% total commission</span>
            </p>
          )}
          {cashbackError && (
            <p className="text-sm text-red-500 mt-2">{cashbackError}</p>
          )}
        </div>

        {/* Optional Constraints Section */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-sm font-medium text-gray-700 mb-4">
            Optional Promotion Constraints
          </h3>
          <p className="text-xs text-gray-500 mb-4">
            Leave these fields empty if the promotion should apply without restrictions.
          </p>
        </div>

        {/* Date Constraints */}
        <div>
          <div className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">
            Date Constraints
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="relative">
              <TextField
                type="datetime-local"
                id="start-date"
                name="start-date"
                label="Start Date & Time (Copenhagen)"
                value={formatDateTime(formData.startDateTime)}
                onChange={handleDateChange('startDateTime')}
                className={`pr-10 ${formData.startDateTime ? 'border-primary' : ''}`}
              />
              {formData.startDateTime && (
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, startDateTime: null }))}
                  className="absolute right-3 bottom-[1.1rem] z-10 text-gray-400 hover:text-gray-600"
                >
                  <CloseIcon className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="relative">
              <TextField
                type="datetime-local"
                id="end-date"
                name="end-date"
                label="End Date & Time (Copenhagen)"
                value={formatDateTime(formData.endDateTime)}
                onChange={handleDateChange('endDateTime')}
                className={`pr-10 ${formData.endDateTime ? 'border-primary' : ''}`}
              />
              {formData.endDateTime && (
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, endDateTime: null }))}
                  className="absolute right-3 bottom-[1.1rem] z-10 text-gray-400 hover:text-gray-600"
                >
                  <CloseIcon className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
          {dateTimeError && (
            <p className="text-sm text-red-500 mt-2">{dateTimeError}</p>
          )}
        </div>

        {/* Time of Day Constraints */}
        <div>
          <div className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">
            Time of Day Constraints
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="relative">
              <TextField
                type="time"
                id="min-time"
                name="min-time"
                label="Minimum Time of Day (Copenhagen)"
                value={formatTimeOfDay(formData.minTimeOfDay)}
                onChange={handleTimeOfDayChange('minTimeOfDay')}
                className={`pr-10 ${formData.minTimeOfDay ? 'border-primary' : ''}`}
              />
              {formData.minTimeOfDay && (
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, minTimeOfDay: null }))}
                  className="absolute right-3 bottom-[1.1rem] z-10 text-gray-400 hover:text-gray-600"
                >
                  <CloseIcon className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="relative">
              <TextField
                type="time"
                id="max-time"
                name="max-time"
                label="Maximum Time of Day (Copenhagen)"
                value={formatTimeOfDay(formData.maxTimeOfDay)}
                onChange={handleTimeOfDayChange('maxTimeOfDay')}
                className={`pr-10 ${formData.maxTimeOfDay ? 'border-primary' : ''}`}
              />
              {formData.maxTimeOfDay && (
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, maxTimeOfDay: null }))}
                  className="absolute right-3 bottom-[1.1rem] z-10 text-gray-400 hover:text-gray-600"
                >
                  <CloseIcon className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Transaction Amount Constraints */}
        <div>
          <div className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">
            Transaction Amount Constraints
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="relative">
              <TextField
                type="number"
                id="min-amount"
                name="min-amount"
                label="Minimum Transaction Amount (DKK)"
                value={formData.minTransactionAmount ?? ''}
                onChange={handleAmountChange('minTransactionAmount')}
                min={0}
                step={1}
                className={`pr-10 ${formData.minTransactionAmount !== null && formData.minTransactionAmount !== undefined ? 'border-primary' : ''}`}
              />
              {formData.minTransactionAmount !== null && formData.minTransactionAmount !== undefined && (
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, minTransactionAmount: null }))}
                  className="absolute right-3 bottom-[1.1rem] z-10 text-gray-400 hover:text-gray-600"
                >
                  <CloseIcon className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="relative">
              <TextField
                type="number"
                id="max-amount"
                name="max-amount"
                label="Maximum Transaction Amount (DKK)"
                value={formData.maxTransactionAmount ?? ''}
                onChange={handleAmountChange('maxTransactionAmount')}
                min={0}
                step={1}
                className={`pr-10 ${formData.maxTransactionAmount !== null && formData.maxTransactionAmount !== undefined ? 'border-primary' : ''}`}
              />
              {formData.maxTransactionAmount !== null && formData.maxTransactionAmount !== undefined && (
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, maxTransactionAmount: null }))}
                  className="absolute right-3 bottom-[1.1rem] z-10 text-gray-400 hover:text-gray-600"
                >
                  <CloseIcon className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
          {transactionAmountError && (
            <p className="text-sm text-red-500 mt-2">{transactionAmountError}</p>
          )}
        </div>
      </div>

      <div className="flex gap-3 justify-end mt-6">
        <Button type="button" variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button type="button" onClick={handleSave} disabled={!isValid}>
          {mode === 'add' ? 'Add Promotion' : 'Save Changes'}
        </Button>
      </div>
    </Modal>
  );
};
